import React from 'react';
import cn from 'classnames';
import { IBaseElementProps } from 'tbk-components/src/components/BasicElement';
import Button, { ILink } from 'tbk-components/src/components/Button';
import Icon from 'tbk-components/src/components/Icon';
import Image, { IImage } from 'tbk-components/src/components/Image';

export interface ITileProps {
	/**
	 * Heading
	 */
	heading?: ILink;
	/**
	 * Background Image - suggested size 48px x 48px
	 */
	headingImage?: IImage;
	/**
	 * Posted
	 */
	posted?: string;
	/**
	 * Background Image - suggested size 328px x 185px
	 */
	image?: IImage;
	/**
	 * Description
	 * @control richtext
	 */
	description?: string;
	/**
	 * CTA
	 */
	cta?: ILink;
}

export interface ISocialComponentProps extends IBaseElementProps {
	/**
	 * Heading
	 */
	heading?: string;
	/**
	 * Tiles
	 */
	tiles?: ITileProps[];
	/**
	 * CTA
	 */
	cta?: ILink;
	/**
	 * CTA icon
	 */
	ctaIcon?: string;
	inverted?: boolean;
}

/**
 * Calian Social Component
 * @icon welcome-widgets-menus
 * @block
 */
const SocialComponent: React.FC<ISocialComponentProps> = ({
	id,
	className,
	classNames = [],
	heading,
	tiles = [],
	cta,
	ctaIcon,
	inverted = false,
}) => {
	return (
		<div
			id={id}
			className={
				className ||
				cn('calian-social-component py-5 py-md-12 px-3', ...classNames)
			}
		>
			<div className="calian-social-component__container container-fluid position-relative mw-1440px p-0">
				{heading && (
					<div className="calian-social-component__heading d-flex justify-content-center mb-4 mb-md-8">
						<h2 className="text-center">{heading}</h2>
					</div>
				)}
				{tiles && tiles.length > 0 && (
					<div className="calian-social-component__tiles gap-3 gap-md-5">
						{tiles.map((tile, index) => (
							<div
								key={index}
								className="calian-social-component__tile d-flex flex-column gap-3 gap-md-4 p-5 px-md-3 ps-md-5 pe-md-5"
							>
								{tile.heading?.label && (
									<a
										href={tile.heading.href}
										target={tile.heading.target}
										rel={
											tile.heading?.target === '_blank'
												? 'noopener noreferrer'
												: ''
										}
										className=" calian-social-component__tile-heading d-flex align-items-center text-decoration-none"
									>
										<div className="calian-social-component__tile-heading-image">
											<Image
												{...tile.headingImage}
												className="img-fluid"
											/>
										</div>
										<div className="ps-3">
											{heading && (
												<h3 className="text-decoration-underline mb-0">
													{tile.heading?.label}
												</h3>
											)}
											{tile.posted && (
												<div className="calian-social-component__tile-posted text-decoration-none lh-1 mt-2">
													{tile.posted}
												</div>
											)}
										</div>
									</a>
								)}
								{tile.image && (
									<div className="calian-social-component__tile-image  me-md-5">
										<Image
											className="img-fluid"
											{...tile.image}
										/>
									</div>
								)}
								{tile.description && (
									<div
										className="calian-social-component__tile-description me-md-5"
										dangerouslySetInnerHTML={{
											__html: tile.description,
										}}
									/>
								)}
								{tile.cta?.label && (
									<div>
										<a
											href={tile.cta.href}
											aria-label={tile.cta.label}
											target={tile.cta.target}
											rel={
												tile.cta?.target === '_blank'
													? 'noopener noreferrer'
													: ''
											}
											className="calian-social-component__tile-cta text-primary text-decoration-none"
										>
											<Icon name={'arrow-right'} />
										</a>
									</div>
								)}
							</div>
						))}
					</div>
				)}
				{cta?.label && (
					<div className="d-flex justify-content-center mt-5">
						<a
							href={cta.href}
							className={cn(
								`btn btn-${inverted ? 'secondary' : 'primary'}`,
							)}
							target={cta.target}
							rel={
								cta?.target === '_blank'
									? 'noopener noreferrer'
									: ''
							}
						>
							{ctaIcon && <Icon name={ctaIcon} />}
							<span className="ps-2">{cta.label}</span>
						</a>
					</div>
				)}
			</div>
		</div>
	);
};
export default SocialComponent;
